import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS - Based on NTV Homepage Design
const PRIMARY = {
  lighter: '#dbeafe',
  light: '#93c5fd',
  main: '#3b82f6',
  dark: '#2563eb',
  darker: '#1d4ed8',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#f3e8ff',
  light: '#d8b4fe',
  main: '#a855f7',
  dark: '#9333ea',
  darker: '#7c3aed',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#dbeafe',
  light: '#93c5fd',
  main: '#3b82f6',
  dark: '#2563eb',
  darker: '#1d4ed8',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#dcfce7',
  light: '#86efac',
  main: '#22c55e',
  dark: '#16a34a',
  darker: '#15803d',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#fef3c7',
  light: '#fcd34d',
  main: '#f59e0b',
  dark: '#d97706',
  darker: '#b45309',
  contrastText: '#fff',
};

const ERROR = {
  lighter: '#fee2e2',
  light: '#fca5a5',
  main: '#ef4444',
  dark: '#dc2626',
  darker: '#b91c1c',
  contrastText: '#fff',
};

const GREY = {
  0: '#FFFFFF',
  100: '#f9fafb',
  200: '#f3f4f6',
  300: '#e5e7eb',
  400: '#d1d5db',
  500: '#9ca3af',
  600: '#6b7280',
  700: '#4b5563',
  800: '#374151',
  900: '#1f2937',
  500_8: alpha('#9ca3af', 0.08),
  500_12: alpha('#9ca3af', 0.12),
  500_16: alpha('#9ca3af', 0.16),
  500_24: alpha('#9ca3af', 0.24),
  500_32: alpha('#9ca3af', 0.32),
  500_48: alpha('#9ca3af', 0.48),
  500_56: alpha('#9ca3af', 0.56),
  500_80: alpha('#9ca3af', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  secondary: createGradient(SECONDARY.light, SECONDARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  // NTV Brand Gradients
  brand: `linear-gradient(135deg, ${PRIMARY.main}, ${SECONDARY.main})`,
  brandReverse: `linear-gradient(135deg, ${SECONDARY.main}, ${PRIMARY.main})`,
  subtle: `linear-gradient(135deg, ${PRIMARY.lighter}, ${SECONDARY.lighter})`,
};

const CHART_COLORS = {
  violet: ['#a855f7', '#c084fc', '#d8b4fe', '#f3e8ff'],
  blue: ['#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'],
  green: ['#22c55e', '#4ade80', '#86efac', '#dcfce7'],
  yellow: ['#f59e0b', '#fbbf24', '#fcd34d', '#fef3c7'],
  red: ['#ef4444', '#f87171', '#fca5a5', '#fee2e2'],
};

const COMMON = {
  common: { black: '#1f2937', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: '#fff' },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { 
      primary: GREY[800], 
      secondary: GREY[600], 
      disabled: GREY[500] 
    },
    background: { 
      paper: '#fff', 
      default: '#fff', 
      neutral: GREY[100],
      // NTV Brand Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.05)}, ${alpha(SECONDARY.main, 0.05)})`,
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { 
      primary: '#fff', 
      secondary: GREY[400], 
      disabled: GREY[600] 
    },
    background: { 
      paper: GREY[800], 
      default: GREY[900], 
      neutral: GREY[500_16],
      // NTV Brand Dark Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.dark, 0.1)}, ${alpha(SECONDARY.dark, 0.1)})`,
    },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;